// Validação dos arquivos do feed ao vivo (§14.7): estrutura pelos schemas zod/mini + invariantes semânticas I-01…I-20
// (e a parte da I-26 que cabe ao evento insight_state), com os códigos E_*. Funções puras: nada de relógio do
// aparelho (só serverNowMs, do cabeçalho Date da resposta). Os tipos, os limites e as checagens do envelope comum (com a
// I-22) moram em validate.core.ts; este arquivo reexporta o que já exportava.
// Este é o módulo que o site ao vivo importa, e por isso NÃO conhece os validadores do acervo e dos insights (I-21 a
// I-28, em validate.replay.ts, validate.sections.ts e validate.insights.ts): os schemas deles ficam fora do JS crítico
// (B01, §15.7). O registro de todos os validadores, com validateFile, é validate.registry.ts (scripts e testes).
import { type ArchiveIndex, ArchiveIndexSchema } from './archive';
import { type CandidateNumber, PLACE_CODES, type PlaceCode, tMin, UF_CODES, type UfCode } from './base';
import {
  ELECTION_EVENT_TYPES,
  type ElectionEvent,
  ElectionEventSchema,
  type EventList,
  EventListSchema,
  eventId,
} from './events';
import { type History, type HistoryPoint, HistoryPointSchema, HistorySchema, SameSectionsSchema } from './history';
import { insightAreaProblem, insightPlaceKey, insightTransitionAllowed } from './insights.core';
import { type MunicipalColumns, MunicipalColumnsSchema, type StateDetail, StateDetailSchema } from './municipal';
import {
  MODEL_EVENT_REASONS,
  type ProbabilityDetail,
  ProbabilityDetailSchema,
  type ProbabilityPayload,
  ProbabilityPayloadSchema,
  PUBLIC_REASONS,
  type Quantiles,
  type VoteQuantiles,
} from './probability';
import { HeldPlaceSchema, NoticeSchema, type Ops, type Snapshot, SnapshotSchema } from './snapshot';
import { type CandidateList, CandidateListSchema } from './static';
import type { Decision, PlaceResult } from './tally';
import {
  checkHeader,
  checkSchemaName,
  checkTMin,
  type DroppedField,
  Issues,
  LIMITS,
  type Validated,
  type ValidationCode,
  type ValidationContext,
  zodIssues,
} from './validate.core';

export type {
  DroppedField,
  Validated,
  ValidationCode,
  ValidationConfig,
  ValidationContext,
  ValidationIssue,
} from './validate.core';
export { LIMITS } from './validate.core';

const PROB_EPS = 1e-6;
const RELOAD_BUILD_RE = /^[A-Za-z0-9._-]{4,64}$/;
// biome-ignore lint/suspicious/noControlCharactersInRegex: texto puro (I-16) proíbe caracteres de controle
const PLAIN_TEXT_BAD = /[<>\u0000-\u001f\u007f]/;

/** I-18: tamanho e JSON bem formado. */
export function parseFeedJson(text: string, byteLength?: number): Validated<unknown> {
  const bytes = byteLength ?? new TextEncoder().encode(text).length;
  if (bytes > LIMITS.maxResponseBytes) {
    return { ok: false, errors: [{ code: 'E_SIZE', path: '', detail: `${bytes} bytes > ${LIMITS.maxResponseBytes}` }] };
  }
  try {
    return { ok: true, value: JSON.parse(text) as unknown, warnings: [], dropped: [] };
  } catch (e) {
    return { ok: false, errors: [{ code: 'E_JSON', path: '', detail: (e as Error).message }] };
  }
}

const sumVotes = (r: Record<string, number> | undefined) => Object.values(r ?? {}).reduce((s, v) => s + v, 0);

/** I-06, I-07, I-09 num PlaceResult. */
function checkPlace(p: PlaceResult, path: string, warnings: Set<ValidationCode>, issues: Issues): void {
  const counts: Array<[string, number]> = [
    ['sections', p.sections],
    ['sections_counted', p.sections_counted],
    ['electorate', p.electorate],
    ['electorate_counted', p.electorate_counted],
    ['turnout', p.turnout],
    ['blank_votes', p.blank_votes],
    ['null_votes', p.null_votes],
    ['valid_votes', p.valid_votes],
    ...Object.entries(p.votes).map(([k, v]): [string, number] => [`votes.${k}`, v]),
    ...Object.entries(p.annulled_votes ?? {}).map(([k, v]): [string, number] => [`annulled_votes.${k}`, v]),
  ];
  for (const [k, v] of counts)
    if (!Number.isInteger(v) || v < 0) issues.add('E_RANGE', `${path}.${k}`, `${v} não é inteiro ≥ 0`);
  if (p.sections <= 0) issues.add('E_RANGE', `${path}.sections`, 'sections deve ser > 0');
  if (p.sections_counted > p.sections) issues.add('E_RANGE', `${path}.sections_counted`, 'maior que sections');
  if (p.electorate_counted > p.electorate * (1 + LIMITS.electorateTolRel) + 10) {
    issues.add('E_RANGE', `${path}.electorate_counted`, 'maior que electorate (com folga)');
  }
  if (p.valid_votes !== sumVotes(p.votes)) issues.add('E_SUM', `${path}.valid_votes`, 'valid_votes ≠ Σ votes');
  const s = p.valid_votes + p.blank_votes + p.null_votes + sumVotes(p.annulled_votes);
  if (s > p.turnout * (1 + LIMITS.sumTolRel) + LIMITS.sumTolAbs) {
    issues.add('E_SUM', `${path}.turnout`, 'válidos + brancos + nulos (+ anulados) acima do comparecimento');
  } else if (s < p.turnout - Math.max(LIMITS.sumTolAbs, LIMITS.sumTolRel * p.turnout)) {
    warnings.add('E_SUM');
  }
  if (p.turnout > p.electorate_counted * (1 + LIMITS.turnoutTolRel) + LIMITS.turnoutTolAbs) {
    issues.add('E_SUM', `${path}.turnout`, 'comparecimento acima dos aptos totalizados');
  }
  for (const k of Object.keys(p.annulled_votes ?? {})) {
    if (k in p.votes) issues.add('E_SUM', `${path}.annulled_votes.${k}`, 'mesma chave em votes e annulled_votes');
  }
  const expected = p.sections_counted === 0 ? 'no_data' : p.sections_counted === p.sections ? 'complete' : 'counting';
  if (p.status !== expected) issues.add('E_STATUS', `${path}.status`, `esperado ${expected}, veio ${p.status}`);
}

const pairEq = (a: readonly string[], b: readonly string[]) => a.length === 2 && a[0] === b[0] && a[1] === b[1];

/** I-12: decisões coerentes no próprio snapshot. */
function checkDecisions(
  p: PlaceResult,
  pair: readonly [CandidateNumber, CandidateNumber],
  path: string,
  issues: Issues,
) {
  for (const key of ['math_decision', 'tse_result'] as const) {
    const d = p[key];
    if (d && !pair.includes(d.winner)) issues.add('E_DECISION', `${path}.${key}`, `vencedor ${d.winner} fora do par`);
  }
  const md = p.math_decision;
  if (md && pair.includes(md.winner)) {
    const other = pair[0] === md.winner ? pair[1] : pair[0];
    const lead = (p.votes[md.winner] ?? 0) - (p.votes[other] ?? 0);
    if (!(lead > p.electorate - p.electorate_counted)) {
      issues.add('E_DECISION', `${path}.math_decision`, 'a regra de §14.3 não se cumpre neste snapshot');
    }
  }
}

/** I-12: decisão presente no atual não some nem troca de vencedor na mesma época. */
function checkDecisionPermanence(prev: PlaceResult | undefined, next: PlaceResult, path: string, issues: Issues) {
  if (!prev) return;
  for (const key of ['math_decision', 'tse_result'] as const) {
    const a: Decision | null = prev[key];
    const b: Decision | null = next[key];
    if (a && (!b || b.winner !== a.winner))
      issues.add('E_DECISION', `${path}.${key}`, 'decisão sumiu ou trocou de vencedor');
  }
}

function quantilesOrdered(q: Quantiles): boolean {
  const seq = [q.q025, q.q10, q.q25, q.q50, q.q75, q.q90, q.q975].filter((v): v is number => v !== undefined);
  return seq.every((v, i) => i === 0 || (seq[i - 1] as number) <= v + PROB_EPS);
}

/** P-35: faixa de votos em ordem, inteira e sem negativo. */
function votesOrdered(q: VoteQuantiles): boolean {
  return q.q10 >= 0 && q.q10 <= q.q50 && q.q50 <= q.q90;
}

/** I-14 e I-15: bloco de probabilidade; inválido é descartado sem derrubar o snapshot. Devolve o motivo ou null. */
export function probabilityProblem(raw: unknown, seq: number): string | null {
  if (raw === null) return null;
  const parsed = ProbabilityPayloadSchema.safeParse(raw);
  if (!parsed.success) return 'estrutura';
  const p = parsed.data as ProbabilityPayload;
  if (p.base_seq > seq) return 'base_seq > seq (I-15)';
  if (p.public_reason !== null) {
    if (p.public_reason.length > LIMITS.publicReasonMaxChars || PLAIN_TEXT_BAD.test(p.public_reason)) {
      return 'public_reason longo ou com < > (I-14)';
    }
    if (!(PUBLIC_REASONS as readonly string[]).includes(p.public_reason))
      return 'public_reason fora do catálogo (P-04)';
  }
  const active = p.status === 'active';
  if (!active && (p.win_prob !== null || p.final_share !== null || (p.final_votes ?? null) !== null))
    return 'win_prob/final_share/final_votes fora de active';
  if (p.win_prob) {
    const values = Object.values(p.win_prob);
    if (values.some((v) => !(v >= 0 && v <= 1))) return 'probabilidade fora de [0, 1]';
    if (Math.abs(values.reduce((s, v) => s + v, 0) - 1) > PROB_EPS) return 'Σ win_prob ≠ 1';
    const [favorite] = Object.entries(p.win_prob).sort((a, b) => b[1] - a[1])[0] ?? [];
    if (p.favorite !== null && p.favorite !== favorite) return 'favorite incoerente com win_prob';
  }
  for (const q of Object.values(p.final_share ?? {})) if (!quantilesOrdered(q)) return 'quantis fora de ordem';
  for (const q of Object.values(p.final_votes ?? {})) if (!votesOrdered(q)) return 'faixa de votos fora de ordem';
  if (active && (p.band === null || p.win_prob === null)) return 'active sem band ou win_prob';
  return null;
}

/** I-16: campos de ops validados um a um (M9). */
function cleanOps(
  ops: Snapshot['ops'] & { notice: unknown; poll_hint_s?: unknown; held?: unknown },
  dropped: DroppedField[],
): Ops {
  const out: Ops = { reload_build: ops.reload_build, notice: null, source_status: ops.source_status };
  if (ops.notice !== null) {
    const n = NoticeSchema.safeParse(ops.notice);
    if (n.success && n.data.text.length <= LIMITS.noticeMaxChars && !PLAIN_TEXT_BAD.test(n.data.text)) {
      out.notice = n.data;
    } else {
      dropped.push('ops.notice');
    }
  }
  if (ops.reload_build !== null && !RELOAD_BUILD_RE.test(ops.reload_build)) {
    out.reload_build = null;
    dropped.push('ops.reload_build');
  }
  if (typeof ops.poll_hint_s === 'number' && Number.isFinite(ops.poll_hint_s)) {
    const [lo, hi] = LIMITS.pollHintBoundsS;
    out.poll_hint_s = Math.min(hi, Math.max(lo, ops.poll_hint_s));
  }
  // P-33: cada lugar parado é conferido sozinho; o que foge à tabela de lugares sai, e o resto fica
  if (ops.held !== undefined) {
    const list = Array.isArray(ops.held) ? ops.held : [];
    const held = list.flatMap((item) => {
      const h = HeldPlaceSchema.safeParse(item);
      return h.success && insightAreaProblem(h.data.scope) === null ? [h.data] : [];
    });
    if (held.length !== list.length || !Array.isArray(ops.held)) dropped.push('ops.held');
    if (held.length > 0) out.held = held;
  }
  return out;
}

export function validateSnapshot(raw: unknown, ctx: ValidationContext): Validated<Snapshot> {
  const issues = new Issues();
  if (!checkSchemaName(raw, 'results.snapshot', issues)) return { ok: false, errors: issues.list };
  const parsed = SnapshotSchema.safeParse(raw);
  if (!parsed.success) {
    zodIssues(parsed.error, issues);
    return { ok: false, errors: issues.list };
  }
  const snap = parsed.data as unknown as Snapshot & { probability: unknown };
  const warnings = new Set<ValidationCode>();
  const dropped: DroppedField[] = [];

  checkHeader(snap, ctx, issues);

  // I-03: aceitação por seq/época contra o atual.
  const cur = ctx.current;
  if (cur && (snap.epoch < cur.epoch || (snap.epoch === cur.epoch && snap.seq < cur.seq))) {
    issues.add(
      'E_SEQ',
      'seq',
      `seq ${snap.seq} (época ${snap.epoch}) anterior ao atual ${cur.seq} (época ${cur.epoch})`,
    );
  }

  // I-10: lugares, pares e candidatos.
  const president = snap.races.president;
  const pair = president.candidates;
  if (!(Number(pair[0]) < Number(pair[1]))) issues.add('E_PLACES', 'races.president.candidates', 'par fora de ordem');
  if (ctx.candidates) {
    const expected = ctx.candidates.president.map((c) => c.number);
    if (!pairEq(pair, expected)) issues.add('E_PLACES', 'races.president.candidates', `esperado ${expected.join(',')}`);
  }
  const placeKeys = Object.keys(president.places);
  for (const code of PLACE_CODES) {
    const p = president.places[code];
    if (!p) issues.add('E_PLACES', `races.president.places.${code}`, 'lugar ausente');
    else if (p.place !== code) issues.add('E_PLACES', `races.president.places.${code}.place`, `veio ${p.place}`);
  }
  if (placeKeys.length !== PLACE_CODES.length) issues.add('E_PLACES', 'races.president.places', 'lugares a mais');
  if (president.national.place !== 'BR') issues.add('E_PLACES', 'races.president.national.place', 'esperado BR');

  const allPresident: Array<[string, PlaceResult]> = [
    ['races.president.national', president.national],
    ...PLACE_CODES.flatMap(
      (c): Array<[string, PlaceResult]> =>
        president.places[c] ? [[`races.president.places.${c}`, president.places[c] as PlaceResult]] : [],
    ),
  ];
  for (const [path, p] of allPresident) {
    for (const k of Object.keys(p.votes))
      if (!pair.includes(k)) issues.add('E_PLACES', `${path}.votes.${k}`, 'fora do par');
    checkPlace(p, path, warnings, issues);
    checkDecisions(p, pair, path, issues);
  }

  const governor = snap.races.governor ?? {};
  for (const [uf, race] of Object.entries(governor) as Array<[string, NonNullable<(typeof governor)[UfCode]>]>) {
    const path = `races.governor.${uf}`;
    if (!(UF_CODES as readonly string[]).includes(uf) || race.place !== uf) {
      issues.add('E_PLACES', path, 'governador só nas UFs (nunca ZZ), com place igual à chave');
      continue;
    }
    if (ctx.candidates) {
      const listed = ctx.candidates.governor[uf as UfCode];
      if (
        !listed ||
        !pairEq(
          race.candidates,
          listed.map((c) => c.number),
        )
      ) {
        issues.add('E_PLACES', `${path}.candidates`, 'par diferente do cadastro');
      }
    }
    for (const k of Object.keys(race.votes))
      if (!race.candidates.includes(k)) issues.add('E_PLACES', `${path}.votes.${k}`, 'fora do par');
    checkPlace(race, path, warnings, issues);
    checkDecisions(race, race.candidates, path, issues);
  }

  // I-11: Brasil à frente das UFs só dentro da folga; reconciliação recalculada.
  const ufs = PLACE_CODES.map((c) => president.places[c]).filter((p): p is PlaceResult => !!p);
  const sum = (k: 'sections_counted' | 'electorate_counted' | 'turnout' | 'valid_votes') =>
    ufs.reduce((s, p) => s + p[k], 0);
  const n = president.national;
  if (n.sections_counted - sum('sections_counted') > Math.max(2, LIMITS.nationalAheadTolRel * n.sections)) {
    issues.add('E_RECON', 'races.president.national.sections_counted', 'Brasil à frente das UFs além da folga');
  }
  const rs = snap.reconciliation.places_sum;
  const recomputed = {
    sections_counted: sum('sections_counted'),
    electorate_counted: sum('electorate_counted'),
    turnout: sum('turnout'),
    valid_votes: sum('valid_votes'),
  };
  const diff = {
    sections: recomputed.sections_counted - n.sections_counted,
    valid_votes: recomputed.valid_votes - n.valid_votes,
  };
  if (
    rs.sections_counted !== recomputed.sections_counted ||
    rs.electorate_counted !== recomputed.electorate_counted ||
    rs.turnout !== recomputed.turnout ||
    rs.valid_votes !== recomputed.valid_votes ||
    snap.reconciliation.diff.sections !== diff.sections ||
    snap.reconciliation.diff.valid_votes !== diff.valid_votes
  ) {
    warnings.add('E_RECON');
    snap.reconciliation = { places_sum: recomputed, diff, places_ahead: snap.reconciliation.places_ahead };
  }

  // I-12: permanência na mesma época.
  if (cur && cur.epoch === snap.epoch) {
    checkDecisionPermanence(cur.races.president.national, n, 'races.president.national', issues);
    for (const c of PLACE_CODES) {
      const next = president.places[c];
      if (next) checkDecisionPermanence(cur.races.president.places[c], next, `races.president.places.${c}`, issues);
    }
    for (const [uf, race] of Object.entries(governor)) {
      if (race) checkDecisionPermanence(cur.races.governor?.[uf as UfCode], race, `races.governor.${uf}`, issues);
    }
  }

  if (!issues.empty) return { ok: false, errors: issues.list };

  // I-14/I-15 e I-16: blocos opcionais descartados sozinhos.
  if (probabilityProblem(snap.probability, snap.seq) !== null) {
    snap.probability = null;
    dropped.push('probability');
  }
  snap.ops = cleanOps(snap.ops as Snapshot['ops'] & { notice: unknown }, dropped);
  return { ok: true, value: snap as Snapshot, warnings: [...warnings], dropped };
}

/** I-13 nas colunas: registry_id, tamanhos e frações. */
export function validateMunicipal(raw: unknown, ctx: ValidationContext): Validated<MunicipalColumns> {
  const issues = new Issues();
  if (!checkSchemaName(raw, 'results.municipal', issues)) return { ok: false, errors: issues.list };
  const parsed = MunicipalColumnsSchema.safeParse(raw);
  if (!parsed.success) {
    zodIssues(parsed.error, issues);
    return { ok: false, errors: issues.list };
  }
  const m = parsed.data as MunicipalColumns;
  checkHeader(m, ctx, issues);
  const warnings: ValidationCode[] = [];
  const geo = ctx.geography;
  if (!geo) {
    warnings.push('E_REGISTRY');
  } else if (m.registry_id !== geo.registry.id) {
    issues.add('E_REGISTRY', 'registry_id', `esperado ${geo.registry.id}`);
  }
  for (const [block, size] of [
    ['brazil', geo?.registry.n_municipalities ?? m.brazil.votes_a.length],
    ['abroad', geo?.registry.n_abroad ?? m.abroad.votes_a.length],
  ] as const) {
    const b = m[block];
    const cols: Array<[string, number[] | undefined]> = [
      ['electorate_fraction', b.electorate_fraction],
      ['sections_fraction', b.sections_fraction],
      ['votes_a', b.votes_a],
      ['votes_b', b.votes_b],
      ['changed_t_min', b.changed_t_min],
    ];
    for (const [name, col] of cols) {
      if (col && col.length !== size) issues.add('E_REGISTRY', `${block}.${name}`, `tamanho ${col.length} ≠ ${size}`);
    }
    for (const name of ['electorate_fraction', 'sections_fraction'] as const) {
      const col = b[name];
      if (col?.some((v) => v < -1 || v > 10000))
        issues.add('E_REGISTRY', `${block}.${name}`, 'fração fora de [−1, 10000]');
    }
    for (const name of ['votes_a', 'votes_b'] as const) {
      if (b[name].some((v) => v < 0)) issues.add('E_RANGE', `${block}.${name}`, 'voto negativo');
    }
  }
  if (!issues.empty) return { ok: false, errors: issues.list };
  return { ok: true, value: m, warnings, dropped: [] };
}

/** I-13 no detalhe por UF: chaves existem na geografia e pertencem ao lugar. */
export function validateStateDetail(raw: unknown, place: PlaceCode, ctx: ValidationContext): Validated<StateDetail> {
  const issues = new Issues();
  if (!checkSchemaName(raw, 'results.state_detail', issues)) return { ok: false, errors: issues.list };
  const parsed = StateDetailSchema.safeParse(raw);
  if (!parsed.success) {
    zodIssues(parsed.error, issues);
    return { ok: false, errors: issues.list };
  }
  const d = parsed.data as StateDetail;
  checkHeader(d, ctx, issues);
  const warnings: ValidationCode[] = [];
  if (d.place !== place) issues.add('E_REGISTRY', 'place', `esperado ${place}`);
  const geo = ctx.geography;
  if (!geo) {
    warnings.push('E_REGISTRY');
  } else {
    if (d.registry_id !== geo.registry.id) issues.add('E_REGISTRY', 'registry_id', `esperado ${geo.registry.id}`);
    const valid =
      place === 'ZZ'
        ? new Set(geo.abroad_cities.map((c) => c.tse))
        : new Set(geo.municipalities.filter((m) => m.uf === place).map((m) => m.ibge));
    const strangers = d.keys.filter((k) => !valid.has(k));
    if (strangers.length > 0)
      issues.add('E_REGISTRY', 'keys', `chaves fora de ${place}: ${strangers.slice(0, 5).join(', ')}`);
  }
  const size = d.keys.length;
  const { votes, ...rest } = d.columns;
  for (const [name, col] of [
    ...Object.entries(rest),
    ...Object.entries(votes).map(([k, v]) => [`votes.${k}`, v] as const),
  ]) {
    if ((col as number[]).length !== size) issues.add('E_REGISTRY', `columns.${name}`, `tamanho ≠ ${size}`);
  }
  if (!issues.empty) return { ok: false, errors: issues.list };
  return { ok: true, value: d, warnings, dropped: [] };
}

/** I-20: pontos fora de ordem ou inválidos são ignorados, não recusam o arquivo. */
export function validateHistory(raw: unknown, ctx: ValidationContext): Validated<History> {
  const issues = new Issues();
  if (!checkSchemaName(raw, 'results.history', issues)) return { ok: false, errors: issues.list };
  const parsed = HistorySchema.safeParse(raw);
  if (!parsed.success) {
    zodIssues(parsed.error, issues);
    return { ok: false, errors: issues.list };
  }
  const h = parsed.data as unknown as Omit<History, 'points'> & { points: unknown[] };
  checkHeader(h, ctx, issues);
  if (!issues.empty) return { ok: false, errors: issues.list };
  const warnings = new Set<ValidationCode>();
  const points: HistoryPoint[] = [];
  for (const rawPoint of h.points) {
    const p = HistoryPointSchema.safeParse(rawPoint);
    if (!p.success) {
      warnings.add('E_SCHEMA');
      continue;
    }
    const point = p.data as unknown as HistoryPoint;
    // Leitura A do DR-31 ([R], rascunho a fechar com o SINAL do motor): bloco opcional, conferido à parte. Fora da
    // estrutura vira null e o ponto fica: análise em rascunho não tira número oficial da linha do tempo.
    if (point.same_sections !== undefined && point.same_sections !== null) {
      const same = SameSectionsSchema.safeParse(point.same_sections);
      point.same_sections = same.success ? same.data : null;
    }
    const t = tMin(point.tse_ht, ctx.config.election_date);
    const last = points[points.length - 1];
    if (!(t >= 0 && t <= 2880)) {
      warnings.add('E_CLOCK');
      continue;
    }
    if (last && point.seq <= last.seq) {
      warnings.add('E_SEQ');
      continue;
    }
    if (last && point.sections_fraction < last.sections_fraction) warnings.add('E_RANGE');
    points.push(point);
  }
  return { ok: true, value: { ...h, points } as History, warnings: [...warnings], dropped: [] };
}

type InsightStateData = Extract<ElectionEvent['data'], { type: 'insight_state' }>;

/**
 * I-26 no evento `insight_state`, sozinho: saliência 1 ou 2, natureza `analysis`, lugar bem formado e de um nível que
 * gera evento (zona, local e seção não geram; no alvo ao vivo, só município e cidade do Exterior: DR-33), escopo igual
 * ao do lugar, uma das cinco transições do cartão e ao menos um sinal (o id de 32 hex de cada um é da estrutura, em
 * events.ts). O fato e a confirmação dele não são de antes das 17h00 da data da noite: é a mesma trava do cartão e do
 * sinal, e o pino da linha do tempo sai de `occurred_at`.
 */
function insightStateOk(ev: ElectionEvent, d: InsightStateData, ctx: ValidationContext): boolean {
  const { level, code } = d.place;
  const abroad = level === 'ABROAD_CITY';
  const date = ctx.config.election_date;
  return (
    ev.salience !== 3 &&
    ev.nature === 'analysis' &&
    Math.min(tMin(ev.occurred_at, date), tMin(ev.detected_at, date)) >= 1020 &&
    insightAreaProblem(d.place) === null &&
    // ao vivo, só por cidade (a regra de isLiveInsightLevel, escrita aqui pelo mesmo motivo do escopo, abaixo)
    (ctx.target === 'replay' || abroad || level === 'MUN') &&
    // O escopo é o do lugar (a regra de insightStateScope, em events.ts, escrita aqui para não levar a função ao JS
    // crítico). Zona, local e seção não têm escopo de evento, e por isso nunca casam.
    ev.scope.level === (abroad ? 'ABROAD' : level) &&
    ev.scope.code === (abroad ? 'ZZ' : code) &&
    insightTransitionAllowed(d.from, d.to, true) &&
    d.signals.length > 0
  );
}

/**
 * I-19: eventos de tipo desconhecido, inválidos, com id fora da fórmula ou repetido são ignorados um a um; e também o
 * evento que não é `insight_state` e traz o nível REGION ou a natureza `analysis`, que só existem para ele (no alvo
 * replay, também para o `math_decision` da noite reconstruída: P-26, item 7). A lista não tem teto de tamanho aqui: os
 * 300 mais recentes são regra do publicador do feed ao vivo, e o acervo traz a noite inteira.
 * I-26 (P-27): o `insight_state` leva a chave do lugar no lugar do código do escopo ('SE' é UF e região) e é ignorado,
 * como os demais, quando foge às regras de insightStateOk. Por lugar valem no máximo dois, na ordem do `seq` (o arquivo
 * vem do mais recente para o mais antigo): a entrada e o desfecho, que parte de `preliminary`; o terceiro e o segundo
 * que não é desfecho são ignorados. Saliência do desfecho diferente da entrada não descarta o evento: sai com a
 * saliência da entrada, com aviso E_RANGE, porque o desfecho tem o mesmo destaque e nunca some (DR-33).
 */
export function validateEvents(raw: unknown, ctx: ValidationContext): Validated<EventList> {
  const issues = new Issues();
  if (!checkSchemaName(raw, 'results.events', issues)) return { ok: false, errors: issues.list };
  const parsed = EventListSchema.safeParse(raw);
  if (!parsed.success) {
    zodIssues(parsed.error, issues);
    return { ok: false, errors: issues.list };
  }
  const list = parsed.data as unknown as Omit<EventList, 'events'> & { events: unknown[] };
  checkHeader(list, ctx, issues);
  if (!issues.empty) return { ok: false, errors: issues.list };
  const warnings = new Set<ValidationCode>();
  const seen = new Set<string>();
  const events: ElectionEvent[] = [];
  for (const rawEvent of list.events) {
    const type = (rawEvent as { type?: unknown } | null)?.type;
    if (!(ELECTION_EVENT_TYPES as readonly unknown[]).includes(type)) continue; // desconhecido: ignorado em silêncio
    const e = ElectionEventSchema.safeParse(rawEvent);
    if (!e.success) {
      warnings.add('E_SCHEMA');
      continue;
    }
    const ev = e.data as ElectionEvent;
    const insight = ev.data.type === 'insight_state' ? ev.data : null;
    // no insight_state, a chave do lugar entra no lugar do código do escopo (insightStateEventId)
    const expectedId = eventId(ev.type, insight ? insightPlaceKey(insight.place) : ev.scope.code, ev.seq);
    if (
      ev.data.type !== ev.type ||
      ev.id !== expectedId ||
      seen.has(ev.id) ||
      // O nível REGION e a natureza `analysis` entraram no contrato com o insight_state e só valem nele: os demais
      // tipos leem `scope.code` como UF (a região 'SE' viraria Sergipe) e não têm frase de análise. Uma exceção, só no
      // alvo replay (P-26, item 7): na noite reconstruída a decisão matemática é a regra de §14.3 aplicada pelo Planeta
      // Voto aos quadros, e não marca do TSE, e por isso o `math_decision` do acervo vem com a natureza `analysis`.
      (insight
        ? !insightStateOk(ev, insight, ctx)
        : ev.scope.level === 'REGION' ||
          (ev.nature === 'analysis' && !(ctx.target === 'replay' && ev.type === 'math_decision')))
    ) {
      warnings.add('E_SCHEMA');
      continue;
    }
    if (
      'reason' in ev.data &&
      ev.data.reason !== null &&
      !(MODEL_EVENT_REASONS as readonly string[]).includes(ev.data.reason)
    ) {
      warnings.add('E_SCHEMA');
      continue;
    }
    seen.add(ev.id);
    events.push(ev);
  }
  // I-26: a entrada e o desfecho de cada lugar, pela ordem do seq. O mapa guarda o primeiro evento do lugar e, depois do
  // segundo, null: o lugar não aceita mais nenhum.
  const first = new Map<string, ElectionEvent | null>();
  const ignored = new Set<ElectionEvent>();
  for (const ev of [...events].sort((a, b) => a.seq - b.seq)) {
    const d = ev.data;
    if (d.type !== 'insight_state') continue;
    const key = insightPlaceKey(d.place);
    const entry = first.get(key);
    if (entry === undefined) first.set(key, ev);
    else if (!entry || d.from !== 'preliminary' || (entry.data as InsightStateData).to !== 'preliminary') {
      ignored.add(ev);
      warnings.add('E_SCHEMA');
    } else {
      if (entry.salience !== ev.salience) {
        ev.salience = entry.salience;
        warnings.add('E_RANGE');
      }
      first.set(key, null);
    }
  }
  const accepted = events.filter((ev) => !ignored.has(ev));
  return { ok: true, value: { ...list, events: accepted } as EventList, warnings: [...warnings], dropped: [] };
}

export function validateArchiveIndex(raw: unknown, ctx: ValidationContext): Validated<ArchiveIndex> {
  const issues = new Issues();
  if (!checkSchemaName(raw, 'results.archive_index', issues)) return { ok: false, errors: issues.list };
  const parsed = ArchiveIndexSchema.safeParse(raw);
  if (!parsed.success) {
    zodIssues(parsed.error, issues);
    return { ok: false, errors: issues.list };
  }
  const a = parsed.data as ArchiveIndex;
  checkHeader(a, ctx, issues);
  a.items.forEach((item, i) => {
    const prev = a.items[i - 1];
    if (prev && item.seq <= prev.seq) issues.add('E_SEQ', `items.${i}.seq`, 'fora de ordem');
    if (item.epoch !== a.epoch) issues.add('E_SEQ', `items.${i}.epoch`, 'item de outra época');
    checkTMin(item.tse_ht, ctx, `items.${i}.tse_ht`, issues);
  });
  if (!issues.empty) return { ok: false, errors: issues.list };
  return { ok: true, value: a, warnings: [], dropped: [] };
}

export function validateProbabilityDetail(raw: unknown, ctx: ValidationContext): Validated<ProbabilityDetail> {
  const issues = new Issues();
  if (!checkSchemaName(raw, 'results.probability', issues)) return { ok: false, errors: issues.list };
  const parsed = ProbabilityDetailSchema.safeParse(raw);
  if (!parsed.success) {
    zodIssues(parsed.error, issues);
    return { ok: false, errors: issues.list };
  }
  const d = parsed.data as ProbabilityDetail;
  checkHeader(d, ctx, issues);
  if (d.base_seq > d.seq) issues.add('E_RANGE', 'base_seq', 'base_seq > seq (I-15)');
  const projections = [
    ['national', d.national],
    ...Object.entries(d.places).map(([k, v]) => [`places.${k}`, v] as const),
    ...Object.entries(d.governor ?? {}).map(([k, v]) => [`governor.${k}`, v] as const),
  ] as const;
  // P-30 item 7: fora de 'active', o detalhe pode levar só o governador, com a projeção nacional vazia
  const nationalEmpty =
    Object.keys(d.national.win_prob).length === 0 && Object.keys(d.national.final_share).length === 0;
  if (nationalEmpty && d.status === 'active') issues.add('E_RANGE', 'national', 'projeção nacional vazia em active');
  for (const [path, p] of projections) {
    if (!p || (path === 'national' && nationalEmpty && d.status !== 'active')) continue;
    const total = Object.values(p.win_prob).reduce((s, v) => s + v, 0);
    if (Math.abs(total - 1) > PROB_EPS || Object.values(p.win_prob).some((v) => v < 0 || v > 1)) {
      issues.add('E_RANGE', `${path}.win_prob`, 'probabilidades fora de [0, 1] ou Σ ≠ 1');
    }
    for (const [c, q] of Object.entries(p.final_share)) {
      if (!quantilesOrdered(q)) issues.add('E_RANGE', `${path}.final_share.${c}`, 'quantis fora de ordem');
    }
    for (const [c, q] of Object.entries(p.final_votes ?? {})) {
      if (!votesOrdered(q)) issues.add('E_RANGE', `${path}.final_votes.${c}`, 'faixa de votos fora de ordem');
    }
  }
  if ('ZZ' in (d.governor ?? {})) issues.add('E_PLACES', 'governor.ZZ', 'governador nunca no Exterior');
  if (!issues.empty) return { ok: false, errors: issues.list };
  return { ok: true, value: d, warnings: [], dropped: [] };
}

/** Cadastro estático: I-10 (gênero não nulo no 2º turno; AC-14.2-02) e pares em ordem. */
export function validateCandidateList(raw: unknown, ctx: Pick<ValidationContext, 'config'>): Validated<CandidateList> {
  const issues = new Issues();
  if (!checkSchemaName(raw, 'static.candidates', issues)) return { ok: false, errors: issues.list };
  const parsed = CandidateListSchema.safeParse(raw);
  if (!parsed.success) {
    zodIssues(parsed.error, issues);
    return { ok: false, errors: issues.list };
  }
  const list = parsed.data as CandidateList;
  const pairs = [
    ['president', list.president],
    ...Object.entries(list.governor).map(([uf, p]) => [`governor.${uf}`, p] as const),
  ] as const;
  for (const [path, p] of pairs) {
    if (!p) continue;
    if (!(Number(p[0].number) < Number(p[1].number)))
      issues.add('E_PLACES', path, 'par fora de ordem (menor primeiro)');
    p.forEach((c, i) => {
      if (ctx.config.round === 2 && c.gender === null)
        issues.add('E_PLACES', `${path}.${i}.gender`, 'gênero nulo no 2º turno');
    });
  }
  if (!issues.empty) return { ok: false, errors: issues.list };
  return { ok: true, value: list, warnings: [], dropped: [] };
}
